<template>
  <!-- Assessment templates: the templates projects start from, by type (US 9398) -->
  <div class="us-page">
    <section class="home-hero us-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('templates_page.eyebrow', { name: getClient.name }) }}</div>
        <h1 class="home-hero__title" qid="assessment-page-title">{{ $t('maintenance.assessments.title') }}</h1>
        <p class="home-hero__summary">{{ summaryText }}</p>

        <div class="home-search us-search">
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input ref="search"
                 v-model="keyword"
                 class="home-search__input"
                 :placeholder="$t('maintenance.assessments.search_placeholder')"
                 :aria-label="$t('maintenance.assessments.search_placeholder')"
                 autocomplete="off"
                 qid="a-search-input"
                 @input="debounceInput"
          />
          <span class="home-kbd">/</span>
        </div>

        <div class="home-hero__chips">
          <button v-if="isGranted(permissions.ASSESSMENT_CREATE)"
                  type="button"
                  class="home-chip home-chip--light"
                  qid="a-create-button"
                  @click="showCreateAModal()"
          >
            <font-awesome-icon icon="plus"/>{{ $t('maintenance.assessments.create_button') }}
          </button>
          <GuideButton v-if="getGuide('assessment_templates_list')" :guide="getGuide('assessment_templates_list')"></GuideButton>
        </div>
      </div>

      <div v-if="allCount" class="home-week at-types" qid="assessment-types">
        <div class="at-types__title">{{ $t('templates_page.by_type') }}</div>
        <div class="at-types__bar">
          <i v-for="type in typeKeys.filter(key => typeCounts[key])"
             :key="type"
             :class="'at-types__part--' + type"
             :style="{ flex: typeCounts[type] }"
          ></i>
        </div>
        <div v-for="type in typeKeys" :key="type" class="at-types__row">
          <span class="at-types__dot" :class="'at-types__part--' + type"></span>
          <span class="us-grow">{{ $t('maintenance.assessments.type.' + type) }}</span>
          <b>{{ typeCounts[type] }}</b>
        </div>
      </div>
    </section>

    <div class="us-bar">
      <div class="us-seg" role="tablist">
        <button v-for="type in ['all'].concat(typeKeys)"
                :key="type"
                type="button"
                role="tab"
                :class="{ 'is-on': filters.type === type }"
                :aria-selected="filters.type === type ? 'true' : 'false'"
                :qid="'a-type-' + type"
                @click="setType(type)"
        >
          {{ $t('templates_page.type_short.' + type) }}
          <em>{{ type === 'all' ? allCount : typeCounts[type] }}</em>
        </button>
      </div>
      <span class="us-grow"></span>
      <multiselect v-if="filters.type !== 'control'"
                   v-model="filters.scope"
                   class="us-filter"
                   :options="scopeOptions"
                   track-by="value"
                   label="name"
                   :placeholder="$t('projects.table.scope')"
                   select-label="✔"
                   selected-label="✔"
                   deselect-label="x"
                   qid="a-filter-scope"
                   @input="loadContent(1)"
      ></multiselect>
    </div>

    <div class="v2-card us-card">
      <div class="us-table-wrap">
        <table class="us-table at-table">
          <thead>
            <tr>
              <th v-for="column in columns"
                  :key="column.key"
                  :class="{ 'is-sortable': column.sort }"
                  :aria-sort="column.sort && selectedSort === column.key ? (selectedDirection === 'asc' ? 'ascending' : 'descending') : null"
                  :qid="'sort-a-' + column.key"
                  @click="column.sort && selectSort(column.key)"
              >
                {{ $t(column.label) }}
                <font-awesome-icon v-if="column.sort && selectedSort === column.key"
                                   :icon="selectedDirection === 'asc' ? 'caret-up' : 'caret-down'"
                                   class="ml-1"
                />
              </th>
              <th class="us-table__actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="object in tableItems"
                :key="object.id"
                class="pt-row"
                qid="a-row"
                @click="openTemplate(object, $event)"
            >
              <td>
                <div class="us-who">
                  <span class="at-icon" :class="'at-icon--' + object.type">
                    <font-awesome-icon :icon="typeIcon(object.type)"/>
                  </span>
                  <span class="pt-name">
                    <router-link :to="viewRoute(object)" class="us-who__name" qid="a-view-page-link">{{ object.name }}</router-link>
                    <span v-if="object.description" class="pt-sub at-desc">{{ object.description }}</span>
                  </span>
                </div>
              </td>
              <td qid="a-type">
                <span class="at-type">
                  <span class="v2-tag" :class="typeTag(object.type)">{{ $t('templates_page.type_short.' + object.type) }}</span>
                  <span v-if="object.scope && object.type !== 'control'" class="v2-tag at-scope">{{ $t('projects.scope.' + $options.filters.slugify(object.scope)) }}</span>
                </span>
              </td>
              <td qid="a-updated">
                <div class="at-updated" v-b-tooltip.hover :title="formatDateTime(object.updated_at)">
                  <span v-if="object.updated_by" class="us-avatar us-avatar--xs" :style="{ backgroundImage: 'url(' + avatarUrl(object.updated_by) + ')' }"></span>
                  <div class="us-last">
                    <span class="us-last__date">{{ object.updated_at | formatRelativeDate }}</span>
                    <span v-if="object.updated_by" class="us-last__ago">{{ object.updated_by.name }}</span>
                  </div>
                </div>
              </td>
              <td class="us-table__actions">
                <b-dropdown right
                            no-caret
                            variant="link"
                            toggle-class="us-icon-btn"
                            qid="a-more-button"
                >
                  <template #button-content>
                    <font-awesome-icon icon="ellipsis-h"/>
                    <span class="sr-only">{{ $t('users_page.actions.more') }}</span>
                  </template>
                  <b-dropdown-item :to="viewRoute(object)" qid="a-open-button">
                    <font-awesome-icon icon="folder-open" class="mr-2"/>{{ $t('templates_page.actions.open') }}
                  </b-dropdown-item>
                  <b-dropdown-item v-if="canEdit(object)" qid="edit-a-modal" @click="showEditAModal(object)">
                    <font-awesome-icon icon="pen" class="mr-2"/>{{ $t('templates_page.actions.edit') }}
                  </b-dropdown-item>
                  <b-dropdown-divider v-if="canDelete(object)"></b-dropdown-divider>
                  <b-dropdown-item v-if="canDelete(object)" variant="danger" qid="delete-a-modal" @click="showConfirmDeleteModal(object)">
                    <font-awesome-icon icon="trash-alt" class="mr-2"/>{{ $t('templates_page.actions.delete') }}
                  </b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length" qid="no-results-row">
              <td :colspan="columns.length + 1" class="us-empty">
                <template v-if="keyword || filters.type !== 'all' || filters.scope">{{ $t('system.no_results') }}</template>
                <div v-else class="pt-empty">
                  <span class="pt-empty__icon"><font-awesome-icon icon="clipboard-list"/></span>
                  <b>{{ $t('templates_page.empty.title') }}</b>
                  <span>{{ $t('templates_page.empty.text') }}</span>
                  <b-button v-if="isGranted(permissions.ASSESSMENT_CREATE)" variant="success" @click="showCreateAModal()">
                    <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('maintenance.assessments.create_button') }}
                  </b-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="totalRows" class="us-foot">
        <span class="us-foot__count">{{ $t('users_page.pagination', { from: rangeFrom, to: rangeTo, total: totalRows }) }}</span>
        <multiselect v-model="perPage"
                     class="us-filter us-filter--small"
                     :options="perPageOptions"
                     track-by="value"
                     label="value"
                     :allow-empty="false"
                     :placeholder="selectedPerPage"
                     :select-label="$t('system.dropdown_select_label')"
                     :selected-label="$t('system.dropdown_selected_label')"
                     :deselect-label="$t('system.dropdown_deselect_label')"
                     qid="a-per-page"
                     @select="perPageChanged"
        ></multiselect>
        <span class="us-grow"></span>
        <b-pagination v-model="currentPage"
                      class="us-pagination"
                      :total-rows="totalRows"
                      :per-page="Number(selectedPerPage)"
                      limit="5"
                      @change="loadContent"
        ></b-pagination>
      </div>
    </div>

    <!-- Delete template -->
    <b-modal ref="delete-assessment" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <h5 class="mb-0" qid="delete-a-title">{{ $t('maintenance.assessments.delete.title') }}</h5>
          <button type="button"
                  class="us-icon-btn"
                  :aria-label="$t('maintenance.assessments.delete.cancel')"
                  @click="hideConfirmDeleteModal"
          >
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="us-remove">
        <span class="at-icon" :class="'at-icon--' + itemToDelete.type">
          <font-awesome-icon :icon="typeIcon(itemToDelete.type)"/>
        </span>
        <div>
          <b>{{ itemToDelete.name }}</b>
          <div v-if="itemToDelete.type" class="us-muted">{{ $t('maintenance.assessments.type.' + itemToDelete.type) }}</div>
        </div>
      </div>
      <p class="mt-3 mb-0">{{ $t('maintenance.assessments.delete.are_you_sure') }}</p>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-a-cancel-button" @click="hideConfirmDeleteModal">
          {{ $t('maintenance.assessments.delete.cancel') }}
        </b-button>
        <b-button v-if="canDelete(itemToDelete)"
                  variant="danger"
                  :disabled="buttonDisabled"
                  qid="delete-a-submit-button"
                  @click="confirmDeleteA(itemToDelete)"
        >
          {{ $t('maintenance.assessments.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Create / edit template -->
    <b-modal ref="template-form" modal-class="v2-modal" size="lg" hide-footer @hide="buttonDisabled = false">
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0" :qid="form.slug ? 'update-a-title' : 'create-a-title'">
              {{ form.slug ? $t('maintenance.assessments.update.title') : $t('maintenance.assessments.create.title') }}
            </h5>
            <GuideButton v-if="form.slug && getGuide('update_assessment_template')" :guide="getGuide('update_assessment_template')"></GuideButton>
          </div>
          <button type="button"
                  class="us-icon-btn"
                  :aria-label="$t('maintenance.data_objects.create.cancel')"
                  @click="hideFormModal"
          >
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>

      <b-alert v-if="error.length" show variant="danger" qid="create-a-error">{{ error }}</b-alert>

      <b-form class="us-form" qid="create-a-form" @submit="formSubmit">
        <div>
          <label class="us-field-label" for="a-form-type">{{ $t('projects.create.type_label') }}<span class="mandatory"> *</span></label>
          <div id="a-form-type" class="at-type-cards" role="radiogroup">
            <label v-for="option in typeOptions"
                   :key="option.value"
                   class="at-type-card"
                   :class="{ 'is-on': form.type === option.value, 'is-locked': !!form.slug && form.type !== option.value }"
                   :qid="'create-a-form-type-' + option.value"
            >
              <input type="radio"
                     class="sr-only"
                     name="template-type"
                     :value="option.value"
                     :checked="form.type === option.value"
                     :disabled="!!form.slug"
                     @change="onChangeType(option.value)"
              >
              <span class="at-icon" :class="'at-icon--' + option.value"><font-awesome-icon :icon="typeIcon(option.value)"/></span>
              <b>{{ option.name }}</b>
              <span>{{ $t('maintenance.assessments.type.' + option.value + '_explainer_text') }}</span>
            </label>
          </div>
        </div>

        <b-form-group class="mb-0">
          <label class="us-field-label" for="a-form-name">{{ $t('maintenance.assessments.create.name_label') }}<span class="mandatory"> *</span></label>
          <b-form-input id="a-form-name"
                        v-model="form.name"
                        type="text"
                        :maxLength="255"
                        required
                        :placeholder="$t('maintenance.assessments.create.name_placeholder')"
                        qid="edit-a-form-name"
          ></b-form-input>
        </b-form-group>

        <b-form-group class="mb-0">
          <label class="us-field-label" for="a-form-description">{{ $t('maintenance.assessments.create.description_label') }}<span class="mandatory"> *</span></label>
          <b-form-textarea id="a-form-description"
                           v-model="form.description"
                           rows="4"
                           max-rows="8"
                           required
                           :placeholder="$t('maintenance.assessments.create.description_placeholder')"
                           qid="edit-a-form-description"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group v-if="form.type === 'risk' || form.type === 'bia'" class="mb-0">
          <label class="us-field-label">{{ $t('projects.create.scope_label') }}<span class="mandatory"> *</span></label>
          <multiselect v-model="scope"
                       :options="scopesFor(form.type)"
                       :placeholder="$t('projects.create.scope_placeholder')"
                       label="name"
                       track-by="value"
                       :select-label="$t('system.dropdown_select_label')"
                       :selected-label="$t('system.dropdown_selected_label')"
                       :deselect-label="$t('system.dropdown_deselect_label')"
                       qid="create-a-form-scope"
          ></multiselect>
        </b-form-group>

        <div class="us-modal-foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="create-a-cancel-button" @click="hideFormModal">
            {{ $t('maintenance.data_objects.create.cancel') }}
          </b-button>
          <b-button type="submit" variant="success" :disabled="buttonDisabled" qid="create-a-submit-button">
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" size="sm" class="fa-spin mr-1"/>
            {{ $t('maintenance.data_objects.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {config} from '@/config.js';
  import {userAvatarUrl} from '@/util/userAvatar.js';
  import GuideButton from '@/components/guide/GuideButton.vue';
  import { TEMPLATE_TYPE_ICONS, TEMPLATE_TYPE_TAGS } from '@/util/templateTypes.js';

  export default {
    name: 'Assessments',
    components: {
      GuideButton
    },
    data: function() {
      return {
        tableItems: [],
        typeKeys: ['control', 'risk', 'bia'],
        typeCounts: { control: 0, risk: 0, bia: 0 },
        columns: [
          { key: 'name', label: 'templates_page.columns.template', sort: true },
          { key: 'type', label: 'templates_page.columns.type_scope', sort: true },
          { key: 'updated_at', label: 'templates_page.columns.last_updated', sort: true }
        ],
        keyword: '',
        filters: {
          type: 'all',
          scope: null
        },
        selectedSort: 'updated_at',
        selectedDirection: 'desc',

        perPageOptions: [
          { value: '10' },
          { value: '20' },
          { value: '50' },
          { value: '100' }
        ],
        perPage: null,
        selectedPerPage: '10',
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        form: {
          slug: '',
          name: '',
          description: '',
          type: 'control'
        },
        scope: null,
        types: [
          { name: this.$t('maintenance.assessments.type.control'), value: 'control' },
          { name: this.$t('maintenance.assessments.type.risk'), value: 'risk' },
          { name: this.$t('maintenance.assessments.type.bia'), value: 'bia' }
        ],
        riskScopes: [
          { name: this.$t('projects.scope.process'), value: 'process' },
          { name: this.$t('projects.scope.activity'), value: 'activity' },
          { name: this.$t('projects.scope.third_party'), value: 'third_party' }
        ],
        biaScopes: [
          { name: this.$t('projects.scope.process'), value: 'process' },
          { name: this.$t('projects.scope.activity'), value: 'activity' }
        ],
        error: '',
        loading: true,
        buttonDisabled: false,
        myGuides: []
      }
    },
    computed: {
      // BIA projects need no template any more (US 9480): existing BIA templates can still be edited
      typeOptions() {
        let editingBia = !!this.form.slug && this.form.type === 'bia';
        return this.types.filter(option => option.value !== 'bia' || editingBia);
      },
      getClient() {
        return this.$store.state.client;
      },
      getStorePerPage() {
        return this.$store.state.perPage;
      },
      allCount() {
        return this.typeCounts.control + this.typeCounts.risk + this.typeCounts.bia;
      },
      summaryText() {
        let text = this.$tc('templates_page.summary', this.allCount, { count: this.allCount });

        if (this.allCount) {
          text += ' ' + this.$t('templates_page.summary_types', {
            control: this.typeCounts.control,
            risk: this.typeCounts.risk,
            bia: this.typeCounts.bia
          });
        }
        return text;
      },
      // Scopes of the selected type, or of all contextual and BIA templates
      scopeOptions() {
        return this.filters.type === 'bia' ? this.biaScopes : this.riskScopes;
      },
      rangeFrom() {
        return (this.currentPage - 1) * Number(this.selectedPerPage) + 1;
      },
      rangeTo() {
        return Math.min(this.rangeFrom + Number(this.selectedPerPage) - 1, this.totalRows);
      }
    },
    watch: {
      getClient: function () {
        this.loadContent(1);
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent(1);
      }, 400),
      typeIcon(type) {
        return TEMPLATE_TYPE_ICONS[type] || 'file-alt';
      },
      typeTag(type) {
        return TEMPLATE_TYPE_TAGS[type] || 'v2-tag--grey';
      },
      avatarUrl(user) {
        return userAvatarUrl(user);
      },
      formatDateTime(date) {
        return this.$options.filters.formatDateTime(date, config.dateFormat + ' HH:mm');
      },
      viewRoute(object) {
        return { name: 'maintenance.assessments.view', params: { slug: object.slug } };
      },
      // The whole row opens the template, except clicks on its buttons and links
      openTemplate(object, event) {
        if (!event.target.closest('a, button, .dropdown')) {
          this.$router.push(this.viewRoute(object));
        }
      },
      canEdit(object) {
        return this.isGranted([this.permissions.ASSESSMENT_EDIT, this.permissions.ASSESSMENT_EDIT_OWN], object.created_by) && (!this.getClient.parent || (this.getClient.parent && object.partition));
      },
      canDelete(object) {
        return this.isGranted([this.permissions.ASSESSMENT_DELETE, this.permissions.ASSESSMENT_DELETE_OWN], object.created_by) && (!this.getClient.parent || (this.getClient.parent && object.partition));
      },
      scopesFor(type) {
        return type === 'bia' ? this.biaScopes : this.riskScopes;
      },
      getGuide(code) {
        return _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance' && object.enabled;
        });
      },
      loadGuides(code) {
        let self = this;
        piincHttp.get('guides', { params: { per_page: 1, language: 'en', type: 'Maintenance', scope: code, client: this.getClient.slug } }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0]);
          }
        }, function() {});
      },
      loadContent(page) {
        let self = this;
        this.currentPage = page || 1;

        piincHttp.get('assessments', { params: {
          page: this.currentPage,
          per_page: this.selectedPerPage,
          sort: this.selectedSort || undefined,
          order: this.selectedDirection || undefined,
          keyword: this.keyword || undefined,
          type: this.filters.type !== 'all' ? this.filters.type : undefined,
          scope: this.filters.scope && this.filters.type !== 'control' ? this.filters.scope.value : undefined,
          with_overview: 1,
          client: this.getClient.slug
        }}).then(function(response) {
          self.tableItems = response.data.items;
          self.totalRows = response.data.total_count;
          self.typeCounts = Object.assign({ control: 0, risk: 0, bia: 0 }, response.data.type_counts);
          self.loading = false;
        }, function() {});
      },
      setType(type) {
        this.filters.type = type;
        if (type === 'control' || (this.filters.scope && !_.find(this.scopeOptions, { value: this.filters.scope.value }))) {
          this.filters.scope = null;
        }
        this.loadContent(1);
      },
      selectSort(sort) {
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          this.selectedDirection = sort === 'updated_at' ? 'desc' : 'asc';
          this.selectedSort = sort;
        }
        this.loadContent(1);
      },
      perPageChanged(perPage) {
        this.selectedPerPage = perPage.value;
        this.$store.commit('storePerPage', this.selectedPerPage);
        this.loadContent(1);
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object;
        this.$refs['delete-assessment'].show();
      },
      hideConfirmDeleteModal() {
        this.$refs['delete-assessment'].hide();
      },
      showCreateAModal() {
        this.error = '';
        this.form = { slug: '', name: '', description: '', type: 'control' };
        this.scope = null;
        this.$refs['template-form'].show();
      },
      showEditAModal(object) {
        this.error = '';
        this.form = { slug: object.slug, name: object.name, description: object.description, type: object.type };
        this.scope = _.find(this.scopesFor(object.type), { value: object.scope }) || null;
        this.loadGuides('update_assessment_template');
        this.$refs['template-form'].show();
      },
      hideFormModal() {
        this.$refs['template-form'].hide();
      },
      onChangeType(type) {
        this.form.type = type;
        this.scope = null;
      },
      formSubmit(evt) {
        let self = this;
        evt.preventDefault();

        if (this.form.type !== 'control' && !(this.scope && this.scope.value)) {
          this.error = this.$t('maintenance.assessments.create.scope_error_message');
          return;
        }

        let data = {
          name: this.form.name,
          description: this.form.description,
          type: this.form.type,
          scope: this.form.type !== 'control' && this.scope ? this.scope.value : '',
          client: this.getClient.slug
        };
        let request;

        if (this.form.slug) {
          request = piincHttp.put('assessments/' + this.form.slug, data);
        } else {
          // Global and contextual templates hold questions; BIA templates hold the impact assessment
          if (data.type === 'bia') {
            data.system_impact_assessment = 1;
          } else {
            data.question = 1;
          }
          request = piincHttp.post('assessments', data);
        }

        this.error = '';
        this.buttonDisabled = true;
        request.then(function(response) {
          self.buttonDisabled = false;
          self.hideFormModal();
          if (self.form.slug) {
            self.loadContent(self.currentPage);
            self.$toastr('success', self.$t('maintenance.assessments.update.success'));
          } else {
            self.$toastr('success', self.$t('maintenance.assessments.create.success'));
            self.$router.push(self.viewRoute(response.data));
          }
        }, function(error) {
          self.buttonDisabled = false;
          self.error = error.data && error.data.message ? error.data.message : '';
        });
      },
      confirmDeleteA(object) {
        let self = this;
        this.buttonDisabled = true;

        piincHttp.delete('assessments/' + object.slug).then(function() {
          self.buttonDisabled = false;
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          self.$toastr('success', self.$t('maintenance.assessments.delete.success'));
        }, function() {
          self.buttonDisabled = false;
        });
      },
      // "/" jumps to the search, unless the user is typing somewhere
      focusSearch(event) {
        let tag = event.target && event.target.tagName;

        if (event.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && !(event.target && event.target.isContentEditable)) {
          event.preventDefault();
          this.$refs.search.focus();
        }
      }
    },
    created() {
      if (this.getStorePerPage) {
        this.selectedPerPage = this.getStorePerPage;
      }
      if (this.getClient.systemModule) {
        this.riskScopes.push({ name: this.$t('projects.scope.system'), value: 'system' });
      }
      this.loadContent();
      this.loadGuides('assessment_templates_list');
    },
    mounted() {
      window.addEventListener('keydown', this.focusSearch);
    },
    beforeDestroy() {
      window.removeEventListener('keydown', this.focusSearch);
    }
  }
</script>
